import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface CreditTransaction {
  id: number;
  company: number | null;
  credit_account: number;
  transaction_type: 'addition' | 'usage' | 'expiry' | 'adjustment';
  amount: number;
  interview: number | null;
  timestamp: string;
  notes: string;
  added_by: number | null;
  company_name: string | null;
  owner_name: string | null;
  expiry_date: string | null;
  is_expired: boolean;
  expired_at: string | null;
}

interface Company {
  id: number;
  name: string;
  status: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: string;
}

interface CreditSummary {
  total_company_credits: number;
  total_company_used: number;
  total_company_remaining: number;
  total_user_credits: number;
  recent_transactions: number;
  expiring_soon: number;
  recently_expired: number;
}

interface AddCreditsFormData {
  owner_type: 'company' | 'user';
  owner_id: number | null;
  amount: number;
  expiry_date: string | null;
  notes: string;
  use_default_expiry: boolean;
}

interface EditTransactionFormData {
  notes: string;
  expiry_date: string | null;
  is_expired: boolean;
}

interface AdjustmentFormData {
  adjustment_amount: number;
  notes: string;
}

interface FormErrors {
  owner_type?: string;
  owner_id?: string;
  amount?: string;
  expiry_date?: string;
  notes?: string;
  use_default_expiry?: string;
}

interface EditFormErrors {
  notes?: string;
  expiry_date?: string;
  is_expired?: string;
}

interface AdjustmentFormErrors {
  adjustment_amount?: string;
  notes?: string;
}

const Credits: React.FC = () => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [creditSummary, setCreditSummary] = useState<CreditSummary>({
    total_company_credits: 0,
    total_company_used: 0,
    total_company_remaining: 0,
    total_user_credits: 0,
    recent_transactions: 0,
    expiring_soon: 0,
    recently_expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('all');
  const [expiryStatusFilter, setExpiryStatusFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [expireCreditsDialogOpen, setExpireCreditsDialogOpen] = useState(false);
  const [editTransactionDialogOpen, setEditTransactionDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  const [editingTransaction, setEditingTransaction] = useState<CreditTransaction | null>(null);
  const [transactionToAdjust, setTransactionToAdjust] = useState<CreditTransaction | null>(null);
  const [transactionToDelete, setTransactionToDelete] = useState<CreditTransaction | null>(null);
  const [formData, setFormData] = useState<AddCreditsFormData>({
    owner_type: 'company',
    owner_id: null,
    amount: 0,
    expiry_date: null,
    notes: '',
    use_default_expiry: true,
  });
  const [editFormData, setEditFormData] = useState<EditTransactionFormData>({
    notes: '',
    expiry_date: null,
    is_expired: false,
  });
  const [adjustmentFormData, setAdjustmentFormData] = useState<AdjustmentFormData>({
    adjustment_amount: 0,
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [editFormErrors, setEditFormErrors] = useState<EditFormErrors>({});
  const [adjustmentFormErrors, setAdjustmentFormErrors] = useState<AdjustmentFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);

  const transactionTypes = [
    { value: 'addition', label: 'Credit Addition', color: 'success' },
    { value: 'usage', label: 'Credit Usage', color: 'error' },
  ];

  const expiryStatuses = [
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'expiring_soon', label: 'Expiring Soon', color: 'warning' },
    { value: 'expired', label: 'Expired', color: 'error' },
  ];

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (transactionTypeFilter !== 'all') {
        params.append('transaction_type', transactionTypeFilter);
      }

      if (expiryStatusFilter !== 'all') {
        params.append('expiry_status', expiryStatusFilter);
      }

      if (ownerFilter !== 'all') {
        params.append('owner_filter', ownerFilter);
      }

      const response = await api.get(`/super-admin/credit-transactions/?${params}`);
      console.log('Transactions response:', response.data);

      setTransactions(response.data.results || response.data);
      setTotalCount(response.data.count || response.data.length);
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setError(
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch transactions'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditSummary = async () => {
    try {
      const response = await api.get('/super-admin/credit-management/credit_summary/');
      setCreditSummary(response.data);
    } catch (error: any) {
      console.error('Error fetching credit summary:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/super-admin/companies/');
      setCompanies(response.data.results || response.data);
    } catch (error: any) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/super-admin/users/?page_size=1000');
      setUsers(response.data.results || response.data);
    } catch (error: any) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchCreditSummary();
    fetchCompanies();
    fetchUsers();
  }, [page, rowsPerPage, searchTerm, transactionTypeFilter, expiryStatusFilter, ownerFilter]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleTransactionTypeFilterChange = (event: any) => {
    setTransactionTypeFilter(event.target.value);
    setPage(0);
  };

  const handleExpiryStatusFilterChange = (event: any) => {
    setExpiryStatusFilter(event.target.value);
    setPage(0);
  };

  const handleOwnerFilterChange = (event: any) => {
    setOwnerFilter(event.target.value);
    setPage(0);
  };

  const handleAddCredits = () => {
    setFormData({
      owner_type: 'company',
      owner_id: null,
      amount: 0,
      expiry_date: null,
      notes: '',
      use_default_expiry: true,
    });
    setFormErrors({});
    setAddCreditsDialogOpen(true);
  };

  const handleExpireCredits = () => {
    if (selectedTransactions.length === 0) {
      alert('Please select transactions to expire');
      return;
    }
    setExpireCreditsDialogOpen(true);
  };

  const handleEditTransaction = (transaction: CreditTransaction) => {
    setEditingTransaction(transaction);
    setEditFormData({
      notes: transaction.notes || '',
      expiry_date: transaction.expiry_date ? transaction.expiry_date.split('T')[0] : null,
      is_expired: transaction.is_expired,
    });
    setEditFormErrors({});
    setEditTransactionDialogOpen(true);
  };

  const handleAdjustTransaction = (transaction: CreditTransaction) => {
    if (transaction.transaction_type !== 'addition') {
      alert('Adjustments can only be made to addition transactions');
      return;
    }
    setTransactionToAdjust(transaction);
    setAdjustmentFormData({
      adjustment_amount: 0,
      notes: '',
    });
    setAdjustmentFormErrors({});
    setAdjustmentDialogOpen(true);
  };

  const handleDeleteTransaction = async (transaction: CreditTransaction) => {
    setTransactionToDelete(transaction);
    setDeleteConfirmDialogOpen(true);
  };

  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      await api.delete(`/super-admin/credit-transactions/${transactionToDelete.id}/`);
      setDeleteConfirmDialogOpen(false);
      setTransactionToDelete(null);
      fetchTransactions();
      fetchCreditSummary();
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction: ' + (error.response?.data?.error || error.message));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.owner_id) {
      errors.owner_id = 'Please select an owner';
    }

    if (!formData.amount || formData.amount <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    if (!formData.use_default_expiry && !formData.expiry_date) {
      errors.expiry_date = 'Please select an expiry date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = (): boolean => {
    const errors: EditFormErrors = {};

    if (editFormData.is_expired && editFormData.expiry_date) {
      const expiryDate = new Date(editFormData.expiry_date);
      const now = new Date();
      if (expiryDate > now) {
        errors.expiry_date = 'Cannot set expiry date in the future when marking as expired';
      }
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAdjustmentForm = (): boolean => {
    const errors: AdjustmentFormErrors = {};

    if (!adjustmentFormData.adjustment_amount || adjustmentFormData.adjustment_amount === 0) {
      errors.adjustment_amount = 'Adjustment amount cannot be zero';
    }

    setAdjustmentFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAddCredits = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData: any = {
        owner_type: formData.owner_type,
        owner_id: formData.owner_id,
        amount: formData.amount,
        notes: formData.notes,
      };

      // Only include expiry_date if not using default expiry
      if (!formData.use_default_expiry && formData.expiry_date) {
        submitData.expiry_date = formData.expiry_date;
      }

      await api.post('/super-admin/credit-management/add_credits_with_expiry/', submitData);
      setAddCreditsDialogOpen(false);
      
      // Add a small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchTransactions();
        fetchCreditSummary();
      }, 500);
    } catch (error: any) {
      console.error('Error adding credits:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add credits';
      
      if (error.response?.data) {
        const fieldErrors: FormErrors = {};
        Object.keys(error.response.data).forEach(key => {
          if (key in formData) {
            fieldErrors[key as keyof FormErrors] = Array.isArray(error.response.data[key]) 
              ? error.response.data[key][0] 
              : error.response.data[key];
          }
        });
        setFormErrors(fieldErrors);
      } else {
        alert(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExpireCredits = async () => {
    try {
      await api.post('/super-admin/credit-management/expire_credits/', {
        transaction_ids: selectedTransactions,
      });
      setExpireCreditsDialogOpen(false);
      setSelectedTransactions([]);
      
      // Add a small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchTransactions();
        fetchCreditSummary();
      }, 500);
    } catch (error: any) {
      console.error('Error expiring credits:', error);
      alert('Failed to expire credits: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmitEditTransaction = async () => {
    if (!validateEditForm() || !editingTransaction) {
      return;
    }

    setEditing(true);

    try {
      const submitData: any = {
        notes: editFormData.notes,
      };

      if (editFormData.expiry_date) {
        submitData.expiry_date = editFormData.expiry_date + 'T00:00:00Z';
      }

      if (editFormData.is_expired !== editingTransaction.is_expired) {
        submitData.is_expired = editFormData.is_expired;
      }

      await api.patch(`/super-admin/credit-transactions/${editingTransaction.id}/`, submitData);
      setEditTransactionDialogOpen(false);
      setEditingTransaction(null);
      fetchTransactions();
      fetchCreditSummary();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update transaction';
      
      if (error.response?.data) {
        const fieldErrors: EditFormErrors = {};
        Object.keys(error.response.data).forEach(key => {
          if (key in editFormData) {
            fieldErrors[key as keyof EditFormErrors] = Array.isArray(error.response.data[key]) 
              ? error.response.data[key][0] 
              : error.response.data[key];
          }
        });
        setEditFormErrors(fieldErrors);
      } else {
        alert(errorMessage);
      }
    } finally {
      setEditing(false);
    }
  };

  const handleSubmitAdjustment = async () => {
    if (!validateAdjustmentForm() || !transactionToAdjust) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        adjustment_amount: adjustmentFormData.adjustment_amount,
        notes: adjustmentFormData.notes,
      };

      await api.post(`/super-admin/credit-transactions/${transactionToAdjust.id}/create_adjustment/`, submitData);
      setAdjustmentDialogOpen(false);
      setTransactionToAdjust(null);
      
      // Add a small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchTransactions();
        fetchCreditSummary();
      }, 500);
    } catch (error: any) {
      console.error('Error creating adjustment:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create adjustment';
      
      if (error.response?.data) {
        const fieldErrors: AdjustmentFormErrors = {};
        Object.keys(error.response.data).forEach(key => {
          if (key in adjustmentFormData) {
            fieldErrors[key as keyof AdjustmentFormErrors] = Array.isArray(error.response.data[key]) 
              ? error.response.data[key][0] 
              : error.response.data[key];
          }
        });
        setAdjustmentFormErrors(fieldErrors);
      } else {
        alert(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = (field: keyof AddCreditsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEditFormChange = (field: keyof EditTransactionFormData, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAdjustmentFormChange = (field: keyof AdjustmentFormData, value: any) => {
    setAdjustmentFormData(prev => ({ ...prev, [field]: value }));
    if (adjustmentFormErrors[field]) {
      setAdjustmentFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getTransactionTypeColor = (type: string) => {
    const transactionType = transactionTypes.find(t => t.value === type);
    return transactionType?.color || 'default';
  };

  const getExpiryStatus = (transaction: CreditTransaction) => {
    if (transaction.transaction_type !== 'addition') return null;
    
    if (transaction.is_expired) return 'expired';
    
    if (transaction.expiry_date) {
      const expiryDate = new Date(transaction.expiry_date);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry <= 0) return 'expired';
      if (daysUntilExpiry <= 7) return 'expiring_soon';
    }
    
    return 'active';
  };

  const getExpiryStatusColor = (status: string | null) => {
    if (!status) return 'default';
    const expiryStatus = expiryStatuses.find(s => s.value === status);
    return expiryStatus?.color || 'default';
  };

  const getExpiryStatusText = (status: string | null) => {
    if (!status) return '';
    const expiryStatus = expiryStatuses.find(s => s.value === status);
    return expiryStatus?.label || '';
  };

  const handleTransactionSelect = (transactionId: number) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionId) 
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(transactions.map(t => t.id));
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Credits Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage credits and expiry for all users and companies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<WarningIcon />}
            onClick={handleExpireCredits}
            disabled={selectedTransactions.length === 0}
          >
            Expire Selected ({selectedTransactions.length})
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCredits}
          >
            Add Credits
          </Button>
        </Box>
      </Box>

      {/* Credit Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Total Credits
                </Typography>
                <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mt: 0.5 }}>
                  {(creditSummary.total_company_credits + creditSummary.total_user_credits).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CreditCardIcon sx={{ color: 'var(--primary-700)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Available Credits
                </Typography>
                <Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
                  {(creditSummary.total_company_remaining + creditSummary.total_user_credits).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--success-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircleIcon sx={{ color: 'var(--success-600)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Expiring Soon
                </Typography>
                <Typography variant="h4" fontWeight={800} color="warning.main" sx={{ mt: 0.5 }}>
                  {creditSummary.expiring_soon}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--warning-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ScheduleIcon sx={{ color: 'var(--warning-600)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Recently Expired
                </Typography>
                <Typography variant="h4" fontWeight={800} color="error.main" sx={{ mt: 0.5 }}>
                  {creditSummary.recently_expired}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--error-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CancelIcon sx={{ color: 'var(--error-600)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transactionTypeFilter}
                  onChange={handleTransactionTypeFilterChange}
                  label="Transaction Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {transactionTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Expiry Status</InputLabel>
                <Select
                  value={expiryStatusFilter}
                  onChange={handleExpiryStatusFilterChange}
                  label="Expiry Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {expiryStatuses.map(status => (
                    <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Owner Type</InputLabel>
                <Select
                  value={ownerFilter}
                  onChange={handleOwnerFilterChange}
                  label="Owner Type"
                >
                  <MenuItem value="all">All Owners</MenuItem>
                  <MenuItem value="company">Companies</MenuItem>
                  <MenuItem value="user">Users</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchTransactions} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Transactions Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Expiry Status</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => {
                const expiryStatus = getExpiryStatus(transaction);
                return (
                  <TableRow key={transaction.id} hover>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(transaction.id)}
                        onChange={() => handleTransactionSelect(transaction.id)}
                        disabled={transaction.transaction_type !== 'addition'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {transaction.owner_name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.company_name || 'Individual User'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transactionTypes.find(t => t.value === transaction.transaction_type)?.label || transaction.transaction_type}
                        color={getTransactionTypeColor(transaction.transaction_type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {(transaction.transaction_type === 'usage' || 
                          (transaction.transaction_type === 'adjustment' && transaction.amount < 0)) ? (
                          <TrendingDownIcon color="error" fontSize="small" />
                        ) : (
                          <TrendingUpIcon color="success" fontSize="small" />
                        )}
                        <Typography variant="body2" color={
                          transaction.transaction_type === 'usage' || 
                          (transaction.transaction_type === 'adjustment' && transaction.amount < 0) 
                            ? 'error.main' 
                            : 'success.main'
                        }>
                          {transaction.transaction_type === 'usage' || (transaction.transaction_type === 'adjustment' && transaction.amount < 0) ? '-' : '+'}{Math.abs(transaction.amount)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {expiryStatus && (
                        <Chip
                          label={getExpiryStatusText(expiryStatus)}
                          color={getExpiryStatusColor(expiryStatus) as any}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.expiry_date ? new Date(transaction.expiry_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transaction.timestamp).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {transaction.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Transaction">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTransaction(transaction)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {transaction.transaction_type === 'addition' && (
                          <Tooltip title="Adjust Credits">
                            <IconButton
                              size="small"
                              onClick={() => handleAdjustTransaction(transaction)}
                              color="warning"
                            >
                              <TrendingUpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Transaction">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTransaction(transaction)}
                            color="error"
                            disabled={transaction.transaction_type === 'addition'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Add Credits Dialog */}
      <Dialog
        open={addCreditsDialogOpen}
        onClose={() => setAddCreditsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Credits with Expiry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <FormControl fullWidth error={!!formErrors.owner_type}>
                <InputLabel>Owner Type</InputLabel>
                <Select
                  value={formData.owner_type}
                  onChange={(e) => handleFormChange('owner_type', e.target.value)}
                  label="Owner Type"
                >
                  <MenuItem value="company">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon />
                      Company
                    </Box>
                  </MenuItem>
                  <MenuItem value="user">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      User
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              {formData.owner_type === 'company' ? (
                <Autocomplete<Company>
                  options={companies}
                  getOptionLabel={(option) => option.name}
                  value={companies.find(c => c.id === formData.owner_id) || null}
                  onChange={(_, value) => handleFormChange('owner_id', value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Owner"
                      error={!!formErrors.owner_id}
                      helperText={formErrors.owner_id}
                      required
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="No companies found"
                />
              ) : (
                <Autocomplete<User>
                  options={users}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                  value={users.find(u => u.id === formData.owner_id) || null}
                  onChange={(_, value) => handleFormChange('owner_id', value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Owner"
                      error={!!formErrors.owner_id}
                      helperText={formErrors.owner_id}
                      required
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="No users found"
                />
              )}
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => handleFormChange('amount', parseInt(e.target.value) || 0)}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.use_default_expiry}
                    onChange={(e) => handleFormChange('use_default_expiry', e.target.checked)}
                  />
                }
                label="Use Default Expiry (30 days)"
              />
            </Box>
            {!formData.use_default_expiry && (
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  type="date"
                  value={formData.expiry_date || ''}
                  onChange={(e) => handleFormChange('expiry_date', e.target.value)}
                  error={!!formErrors.expiry_date}
                  helperText={formErrors.expiry_date}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                error={!!formErrors.notes}
                helperText={formErrors.notes}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCreditsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitAddCredits}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            {submitting ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog
        open={editTransactionDialogOpen}
        onClose={() => setEditTransactionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Credit Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Transaction Details
              </Typography>
              <Typography variant="body1">
                <strong>Type:</strong> {editingTransaction?.transaction_type}
              </Typography>
              <Typography variant="body1">
                <strong>Amount:</strong> {editingTransaction?.amount}
              </Typography>
              <Typography variant="body1">
                <strong>Owner:</strong> {editingTransaction?.owner_name}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={editFormData.notes}
              onChange={(e) => handleEditFormChange('notes', e.target.value)}
              error={!!editFormErrors.notes}
              helperText={editFormErrors.notes}
            />
            {editingTransaction?.transaction_type === 'addition' && (
              <>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  type="date"
                  value={editFormData.expiry_date || ''}
                  onChange={(e) => handleEditFormChange('expiry_date', e.target.value)}
                  error={!!editFormErrors.expiry_date}
                  helperText={editFormErrors.expiry_date}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.is_expired}
                      onChange={(e) => handleEditFormChange('is_expired', e.target.checked)}
                    />
                  }
                  label="Mark as Expired"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTransactionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitEditTransaction}
            variant="contained"
            disabled={editing}
            startIcon={editing ? <CircularProgress size={20} /> : undefined}
          >
            {editing ? 'Updating...' : 'Update Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog
        open={adjustmentDialogOpen}
        onClose={() => setAdjustmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Adjust Credit Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Original Transaction
              </Typography>
              <Typography variant="body1">
                <strong>Type:</strong> {transactionToAdjust?.transaction_type}
              </Typography>
              <Typography variant="body1">
                <strong>Amount:</strong> {transactionToAdjust?.amount}
              </Typography>
              <Typography variant="body1">
                <strong>Owner:</strong> {transactionToAdjust?.owner_name}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Adjustment Amount"
              type="number"
              value={adjustmentFormData.adjustment_amount}
              onChange={(e) => handleAdjustmentFormChange('adjustment_amount', parseInt(e.target.value) || 0)}
              error={!!adjustmentFormErrors.adjustment_amount}
              helperText={adjustmentFormErrors.adjustment_amount || "Positive for adding credits, negative for reducing credits"}
              required
            />
            <TextField
              fullWidth
              label="Adjustment Notes"
              multiline
              rows={3}
              value={adjustmentFormData.notes}
              onChange={(e) => handleAdjustmentFormChange('notes', e.target.value)}
              error={!!adjustmentFormErrors.notes}
              helperText={adjustmentFormErrors.notes || "Explain the reason for this adjustment"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitAdjustment}
            variant="contained"
            color="warning"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            {submitting ? 'Creating Adjustment...' : 'Create Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expire Credits Dialog */}
      <Dialog
        open={expireCreditsDialogOpen}
        onClose={() => setExpireCreditsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Expire Selected Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to expire {selectedTransactions.length} credit transaction(s)?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will mark the selected credit additions as expired. Users will no longer be able to use these credits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpireCreditsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitExpireCredits}
            variant="contained"
            color="warning"
          >
            Expire Credits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Transaction Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this {transactionToDelete?.transaction_type} transaction?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The transaction will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteTransaction}
            variant="contained"
            color="error"
          >
            Delete Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Credits;
